import React, { useCallback, useEffect, useState, useRef } from "react"
import { Button, CircularProgress, Grid } from "@material-ui/core"
import { Alert } from '@material-ui/lab'
import AccessRequestAlert from 'components/shared/AccessRequestAlert'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { useDispatch, useSelector } from "react-redux"
import {
    CreditCheckColumn as bgvColumns,
    buisnessBgvColumns,
} from "constants/application"
import {
    dispatchBgvSearchResult,
    dispatchDetailedBusinessReport, getBgvReportHistory, refreshBgvReport
} from "actions/applicants"
import TableComponent from "components/shared/TableComponent"
import OwnerReport from './OwnerReport/OwnerReport'
import { Toast } from "components/shared/Toaster"
import Drawer from 'components/shared/Drawer'
import AuthService from "tools/AuthService"
import strings from "strings"
import emptyUCC from "styles/images/emptyUCC.svg"
import '../UCC/ucc.scss'
import {useAppContext} from "../../../../../context/AppContext";
import ConfirmationDialog from "../../../../ConfirmationDialog";
import BgvReportActions from "./BgvReportActions";
import BgvReportHistoryDrawer from "./BgvReportHistoryDrawer";
import BusinessDetailReport from './BusinessDetailReport/BusinessDetailReport'
import queryString from "query-string";
import {useHistory} from "react-router-dom";
import CreditCost from 'components/shared/CreditCost'

const bgvString = strings.APPLICANT.BGV
export function Bgv(props) {
    const isMounted = useRef(true);
    const {
        getBgvList,
        bgvList,
        getConnectedBuisnessList,
        connectedBuisnessList,
        applicationId,
        performBgvSearch,
        searchResult: resData,
        saveBgvReport,
        saveBgvReportDemoMode,
        getDetailedBusinessReport,
        detailedBusinessReport: businessReport,
        saveBuisnessReport,
        saveBusinessReportDemoMode,
        getConnectedBuisnessReport,
        saveConnectedBuisnessReport,
        downloadFile,
        attachFile,
        updateSummaryCard,
        getBackgroundSearchSavedReport,
        creditCosts,
        applicationEin,
        suggestedReports,
        onOpenReport
    } = props
    const [selectedRow, setSelectedRow] = useState([])
    const backgroundTableRef = useRef(null)
    const [loading, setLoading] = useState({ list: false, connectedBuisnessList: false, search: false, report: false, save: false, download: false, attach: false })
    const [openReport, setOpenReport] = useState({ business: false, owner: false, detailed: false })
    const [openConnectedBusinessReport, setOpenConnectedBusinessReport] = useState({})
    const [connectedBuisnessLoading, setConnectedBuisnessLoading] = useState({ get: undefined, save: undefined })
    const [isSavedReport, setIsSavedReport] = useState(false)
    const [detailIsConnected, setDetailIsConnected] = useState(false)
    const [saveDetails, setSaveDetails] = useState({ rowData: undefined, latestId: undefined })
    const [pdfReportUuid, setPdfReportUuid] = useState(null)
    const dispatch = useDispatch();
    const { isReadOnlyUser, creditUsageEnabled } = AuthService.getClientFeatures()
    const { connectedBusinessReport } = useSelector(state => state.applicant.bgv)
    const [reportHistory, setReportHistory] = useState([])
    const [historyOpen, setHistoryOpen] = useState(false)
    const [refreshLoading, setRefreshLoading] = useState(false)
    const {isDemoMode} = useAppContext()
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}')
    const currentUser = AuthService.loggedInUser || storedUser
    const showLexisAccessRequest = currentUser?.showLexisAccessRequest === true
    const clientName = currentUser?.securityContext?.client?.clientName || ''
    const clientId = currentUser?.securityContext?.client?.clientId || ''
    const userEmail = currentUser?.email || ''

    const handleRefresh = (row) => {
        openConfirmDialog(
            'Refresh Background Report',
            'This will request a new background report and consume credits. The current report will be preserved in the report history. Continue?',
            () => {
                setRefreshLoading(true)
                const payLoad = {
                    [row.entityType === 'OWNER' ? 'ownerId' : 'businessId']: row.entityUUID,
                    searchByDBA: row.entityType === 'BUSINESS_DBA'
                }
                dispatch(refreshBgvReport(applicationId, payLoad, isDemoMode)).then((res) => {
                    setPdfReportUuid(res?.pdfReportUuid)
                    setIsSavedReport(true)
                    // Close all drawers first
                    setOpenReport({ business: false, owner: false, detailed: false })
                    // Open the correct one on next tick so the drawer re-mounts with new data
                    const isBusiness = row.entityType !== 'OWNER'
                    setTimeout(() => {
                        if (isBusiness) {
                            setOpenReport(prev => ({ ...prev, detailed: true }))
                        } else {
                            setOpenReport(prev => ({ ...prev, owner: true }))
                        }
                    }, 100)
                    getList()
                }).catch(async (err) => {
                    const message = await err?.json?.().then(body => body?.message).catch(() => null)
                    if (err?.status === 404) {
                        Toast({ type: 'error', message: 'No existing report found for this entity.' })
                    } else {
                        Toast({ type: 'error', message: message || 'Failed to refresh report. Please try again.' })
                    }
                }).finally(() => {
                    setRefreshLoading(false)
                })
            }
        )
    }

    // History handler
    const handleViewHistory = (row) => {
        dispatch(getBgvReportHistory(applicationId, row.entityUUID, row.entityType === 'BUSINESS_DBA'))
            .then(res => {
                setReportHistory(res || [])
                setHistoryOpen(true)
            })
    }

    // View specific historical report
    const handleViewHistoricalReport = (report) => {
        setHistoryOpen(false)
        setLoading(prev => ({ ...prev, report: true }))
        getBackgroundSearchSavedReport(applicationId, report.backgroundReportStatusUuid).then(res => {
            setIsSavedReport(true)
            if (res.entityType === 'BUSINESS' || res.entityType === 'CONNECTED_BUSINESS' || res.entityType === 'BUSINESS_DBA') {
                const rowData = res.response?.TopBusinessReportResponseEx?.response?.Businesses?.Business[0]
                if (rowData) {
                    setDetailIsConnected(res.entityType === 'CONNECTED_BUSINESS')
                    dispatch(dispatchDetailedBusinessReport(rowData))
                    setOpenReport(prev => ({ ...prev, detailed: true }))
                }
            } else {
                const rowData = res.response?.BpsReportResponseEx?.response?.Individual
                if (rowData) {
                    dispatch(dispatchBgvSearchResult(rowData))
                    setOpenReport(prev => ({ ...prev, owner: true }))
                }
            }
            setPdfReportUuid(report.pdfReportUuid)
        }).finally(() => {
            setLoading(prev => ({ ...prev, report: false }))
        })
    }

    //Type business or connected business, key to identify which connected business, getFullData for printing raw data
    const getBuinessData = (type, key) => (
        type === 'business' ? businessReport : connectedBusinessReport.find(cbr => cbr.key === key)
    )

    const safeListLoadingSetter = (value) => {
        if (isMounted.current) {
            setLoading(prev => ({ ...prev, list: value }))
        }
    }

    const getList = useCallback((clearSelected = false) => {
        if (isMounted.current) {
            setLoading(prev => ({ ...prev, list: true }))
            if (clearSelected)
                setSelectedRow([])
        }
        // resetting data
        getBgvList(null, true)
        getConnectedBuisnessList(null, true)
        getBgvList(applicationId).finally(() => {
            safeListLoadingSetter(false)
        })
        getConnectedBuisnessList(applicationId).finally(() => {
            safeListLoadingSetter(false)
        })
    }, [getBgvList, getConnectedBuisnessList, applicationId])

    useEffect(() => {
        getList(true)
        return () => {
            isMounted.current = false;
        };
    }, [getList])

    const history = useHistory()

    useEffect(() => {
        const { bgvEntity, bgvEntityType } = queryString.parse(history.location.search)
        if (bgvEntity && bgvList.length > 0) {
            const row = bgvList.find(r => r.entityUUID === bgvEntity && r.entityType === bgvEntityType)
            if (row && row.status === strings.STATUS_NAMES.Hit_Completed) {
                viewSavedReport(row)
                setPdfReportUuid(row.pdfReportUuid)
                // Clean up query params
                const params = queryString.parse(history.location.search)
                delete params.bgvEntity
                delete params.bgvEntityType
                history.replace({ search: queryString.stringify(params) })
            }
        }
    }, [bgvList])

    const nonSelectable = bgvList
        ?.filter(item => item.status === strings.STATUS_NAMES.Hit_Completed)
        .map(item => item.refId);

    const handleOnSelect = (row, isSelect) => {
        if (isSelect) {
            setSelectedRow([row])
            setPdfReportUuid(row.pdfReportUuid)
        }
    }

    const handleSearch = () => {
        setLoading(prev => ({ ...prev, search: true }))
        setIsSavedReport(false)
        const payLoad = {
            [selectedRow[0].entityType === 'OWNER' ? 'ownerId' : 'businessId']: selectedRow[0].entityUUID,
            searchByDBA: selectedRow[0].entityType === 'BUSINESS_DBA'
        }
        performBgvSearch(applicationId, payLoad, isDemoMode).then((res) => {
            switch (res?.productName) {
                case 'BPS_REPORT':
                    setOpenReport(prev => ({ ...prev, owner: true }))
                    break;
                case 'TOP_BIZ_SEARCH':
                    setOpenReport(prev => ({ ...prev, business: true }))
                    break;
                case 'TOP_BIZ_REPORT':
                    setOpenReport(prev => ({ ...prev, detailed: true }))
                    break;
                default:
                    break;
            }
            setPdfReportUuid(res?.pdfReportUuid)
            getList()
        }).finally(() => {
            setLoading(prev => ({ ...prev, search: false }))
        })
    }

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const openConfirmDialog = (title, message, onConfirm) => {
        setConfirmDialog({
            open: true,
            title,
            message,
            onConfirm
        });
    };

    const closeConfirmDialog = () => {
        setConfirmDialog({
            open: false,
            title: '',
            message: '',
            onConfirm: null
        });
    };

    const handleConfirm = () => {
        if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
        }
        closeConfirmDialog();
    };

    // Viewing and saving must send the same DBA flag: the backend caches the report under a key that
    // includes it, so a mismatch makes Save miss the cache and charge Lexis a second time.
    const isDbaSearch = () => selectedRow[0]?.entityType === 'BUSINESS_DBA'

    const handleDetailedReport = (row) => {
        setLoading(prev => ({ ...prev, search: true }))
        setDetailIsConnected(false)
        dispatch(dispatchDetailedBusinessReport({}))
        setPdfReportUuid(null)
        const payLoad = { businessId: applicationId, lexisBusinessIds: row.BusinessIds, searchByDBA: isDbaSearch() }
        getDetailedBusinessReport(applicationId, payLoad, isDemoMode).then(
            (res) => {
                setPdfReportUuid(res?.pdfReportUuid)
            }
        ).finally(() => {
            setLoading(prev => ({ ...prev, search: false }))
        })
        setOpenReport(prev => ({ ...prev, business: false, detailed: true }))
    }

    const viewSavedReport = (row) => {
        setLoading(prev => ({ ...prev, report: true }))
        setIsSavedReport(true)
        setSelectedRow([row])
        getBackgroundSearchSavedReport(applicationId, row.backgroundReportStatusUuid).then(res => {
            if (row.entityType === 'BUSINESS' || row.entityType === 'CONNECTED_BUSINESS' || row.entityType === 'BUSINESS_DBA') {
                const rowData = res.response?.TopBusinessReportResponseEx?.response?.Businesses?.Business?.[0]
                setDetailIsConnected(row.entityType === 'CONNECTED_BUSINESS')
                dispatch(dispatchDetailedBusinessReport(rowData || {}))
                setOpenReport(prev => ({ ...prev, detailed: true }))
            }
            else {
                const rowData = res.response?.BpsReportResponseEx?.response?.Individual
                dispatch(dispatchBgvSearchResult(rowData || {}))
                setOpenReport(prev => ({ ...prev, owner: true }))
            }
        })
        setLoading(prev => ({ ...prev, report: false }))
    }

    const handleOwnerSave = () => {
        if (isReadOnlyUser) return
        setLoading(prev => ({ ...prev, save: true }))
        const payLoad = { [selectedRow[0].entityType === 'OWNER' ? 'ownerId' : 'businessId']: selectedRow[0].entityUUID }
        if (isDemoMode){
            saveBgvReportDemoMode(applicationId, payLoad).then(() => {
                Toast({ type: 'success', message: strings.SUCCESS });
                getList(true)
                setOpenReport(prev => ({ ...prev, owner: false }));
            }).finally(() => {
                updateSummaryCard()
                setLoading(prev => ({ ...prev, save: false }));
            })
        } else {
            saveBgvReport(applicationId, payLoad).then(() => {
                Toast({ type: 'success', message: strings.SUCCESS });
                getList(true)
                setOpenReport(prev => ({ ...prev, owner: false }));
            }).finally(() => {
                updateSummaryCard()
                setLoading(prev => ({ ...prev, save: false }));
            })
        }
    }

    const handleBusinessSave = (row) => {
        if (isReadOnlyUser || !row?.BusinessIds) return
        setLoading(prev => ({ ...prev, save: true }))
        const payLoad = { businessId: applicationId, lexisBusinessIds: row.BusinessIds, searchByDBA: isDbaSearch() }
        if (isDemoMode){
            saveBusinessReportDemoMode(applicationId, payLoad).then(() => {
                Toast({ type: 'success', message: strings.SUCCESS });
                setOpenReport(prev => ({ ...prev, business: false, detailed: false }))
                getList(true)
            }).finally(() => {
                updateSummaryCard()
                setLoading(prev => ({ ...prev, save: false }))
            })
        } else {
            saveBuisnessReport(applicationId, payLoad).then(() => {
                Toast({ type: 'success', message: strings.SUCCESS });
                setOpenReport(prev => ({ ...prev, business: false, detailed: false }))
                getList(true)
            }).finally(() => {
                updateSummaryCard()
                setLoading(prev => ({ ...prev, save: false }))
            })
        }
    }

    const handleConnectedBusinessReport = (row, rowIndex) => {
        setOpenConnectedBusinessReport(prev => ({ ...prev, [row?.BusinessIds?.OrgID]: false }))
        setConnectedBuisnessLoading(prev => ({ ...prev, get: rowIndex }))
        setSaveDetails({ rowData: row, latestId: row?.BusinessIds?.OrgID })
        getConnectedBuisnessReport(applicationId, row, false, isDemoMode)
            .then(setTimeout(() => setOpenConnectedBusinessReport(prev => ({ ...prev, [row?.BusinessIds?.OrgID]: true })), 600))
            .finally(() => setConnectedBuisnessLoading(prev => ({ ...prev, get: undefined })))
    }

    const handleConnectedBusinessSave = (row, rowIndex) => {
        if (isReadOnlyUser) return
        setConnectedBuisnessLoading(prev => ({ ...prev, save: row ? rowIndex : true }))
        saveConnectedBuisnessReport(applicationId, row || saveDetails.rowData, isDemoMode)
            .then(() => {
                Toast({ type: 'success', message: strings.SUCCESS })
                setOpenConnectedBusinessReport(prev => ({ ...prev, [saveDetails.latestId]: false }))
                getList()
            })
            .finally(() => setConnectedBuisnessLoading(prev => ({ ...prev, save: undefined })))
    }

    const handleDownload = () => {
        setLoading(prev => ({ ...prev, download: true }))
        downloadFile(applicationId, pdfReportUuid, 'download.pdf')
            .finally(() => setLoading(prev => ({ ...prev, download: false })))
    }

    const handleAttach = () => {
        setLoading(prev => ({ ...prev, attach: true }))
        attachFile(applicationId, { pdfReportUuid })
            .then(() => Toast({ type: 'success', message: bgvString.ATTACH_SUCCESS_MSG }))
            .finally(() => setLoading(prev => ({ ...prev, attach: false })))
    }

    const selectRow = {
        mode: 'radio',
        clickToSelect: true,
        selected: selectedRow.map(r => r.refId),
        onSelect: handleOnSelect,
        nonSelectable,
        hideSelectAll: true
    }
    const eyeIconAction = (row) => {
        if (row.status === strings.STATUS_NAMES.Hit_Completed) {
            viewSavedReport(row)
            setPdfReportUuid(row.pdfReportUuid)
        }
        if (row.status === strings.STATUS_NAMES.In_Progress) {
            // Reopened searches are viewed and saved for this row (business or its DBA)
            setSelectedRow([row])
            setLoading(prev => ({ ...prev, report: true }))
            getBackgroundSearchSavedReport(applicationId, row.backgroundReportStatusUuid).then(res => {
                if (res.entityType === 'OWNER') {
                    const rowData = res.response?.BpsReportResponseEx?.response?.Individual
                    if (rowData) {
                        dispatch(dispatchBgvSearchResult(rowData))
                    }
                    setOpenReport(prev => ({ ...prev, owner: true }))
                } else {
                    const rowData = res.response?.TopBusinessSearchResponseEx?.response?.Records?.Record || []
                    dispatch(dispatchBgvSearchResult(rowData))
                    setOpenReport(prev => ({ ...prev, business: true }))
                }
            }).finally(() => {
                setLoading(prev => ({ ...prev, report: false }))
            })
        }
    }


    const action = {
        formatter: (cell, row) => {
            return (
                <div className="d-flex align-items-center">
                    {(row.status === strings.STATUS_NAMES.Hit_Completed ||
                        row.status === strings.STATUS_NAMES.In_Progress) && (
                        <VisibilityIcon
                            className='cursor-pointer'
                            onClick={() => eyeIconAction(row)}
                        />
                    )}
                </div>
            )
        }
    }
    bgvColumns.map((item, index) => {
        if (item.dataField === 'action') {
            bgvColumns[index] = { ...item, ...action }
        }
        return item
    })


    function getEndIconBusinessTopReport() {
        if (creditCosts){
            const costObj = creditCosts.find(cost => cost.requestType === 'LEXIS_TOP_BUSINESS_REPORT');
            return costObj ? costObj.creditCost : 0;
        }
        return 0;
    }

    const actionForBgv = {
        formatter: (cell, row, rowIndex) => {
            return (
                <>
                    {/* Already-requested reports are reused without a new charge, so no cost is shown. */}
                    <Button
                        endIcon={creditUsageEnabled && creditCosts && !row.alreadyRequested ?
                            <CreditCost cost={getEndIconBusinessTopReport()} /> : undefined}
                        className="mr-2" variant="outlined" color="primary" size="small" onClick={() => handleDetailedReport(row)}>
                        {row.alreadyRequested ? bgvString.VIEW_REPORT : strings.APPLICANT.DETAIL_REPORT}
                    </Button>
                </>
            )
        }
    }

    buisnessBgvColumns.map((item, index) => {
        if (item.dataField === 'action') {
            buisnessBgvColumns[index] = { ...item, ...actionForBgv }
        }
        return item
    })


    const ownerRows = (bgvList || []).filter(row => row.entityType === 'OWNER')
    const ownerToSearch = !isReadOnlyUser && ownerRows.find(row => row.status !== strings.STATUS_NAMES.Hit_Completed)
    const ownerAction = ownerToSearch ? 'Run owner background check' : ownerRows.length ? 'View owner reports' : ''
    const handleOwnerAction = () => {
        setOpenReport(prev => ({ ...prev, detailed: false }))
        if (ownerToSearch) {
            setSelectedRow([ownerToSearch])
            setPdfReportUuid(ownerToSearch.pdfReportUuid)
        }
        setTimeout(() => backgroundTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
    }

    const buinessReportJSX = (type, key) => (
        loading.search ? (
            <div className='d-flex align-items-center justify-content-center h-100'>
                <CircularProgress size={48} color='primary' />
            </div>
        ) : (
            <BusinessDetailReport
                report={getBuinessData(type, key)}
                applicationEin={type === 'business' && !detailIsConnected ? applicationEin : undefined}
                onGetConnectedReport={handleConnectedBusinessReport}
                connectedLoadingIndex={connectedBuisnessLoading.get}
                connectedCost={creditUsageEnabled && creditCosts ? getEndIconBusinessTopReport() : undefined}
                ownerAction={type === 'business' && !detailIsConnected ? ownerAction : ''}
                onOwnerAction={handleOwnerAction}
                suggestedReports={suggestedReports}
                onOpenReport={onOpenReport && (id => {
                    setOpenReport(prev => ({ ...prev, detailed: false }))
                    onOpenReport(id)
                })}
            />
        )
    )

    const detailedReportActions = (
        <BgvReportActions
            pdfReportUuid={pdfReportUuid}
            loading={loading}
            isSavedReport={isSavedReport}
            isReadOnlyUser={isReadOnlyUser}
            refreshLoading={refreshLoading}
            selectedRow={selectedRow[0]}
            businessReport={businessReport}
            onDownload={handleDownload}
            onAttach={handleAttach}
            onBusinessSave={handleBusinessSave}
            onRefresh={handleRefresh}
            onViewHistory={handleViewHistory}
            creditCosts={creditCosts}    creditUsageEnabled={creditUsageEnabled}
        />
    )

    function getEndIcon() {
        if (!selectedRow[0]){
            return null;
        }
        const requestType = selectedRow[0]?.entityType === 'OWNER' ? 'LEXIS_COMPREHENSIVE_INDIVIDUAL_REPORT' : 'LEXIS_BUSINESS_SEARCH'
        if (creditCosts){
            const costObj = creditCosts.find(cost => cost.requestType === requestType);
            return costObj ? costObj.creditCost : 0;
        }
        return 0;
    }
    if (showLexisAccessRequest) {
        return (
            <Grid container>
                <Grid item xs={12} className='m-3'>
                <AccessRequestAlert
                    type="lexis"
                    clientName={clientName}
                    clientId={clientId}
                    userEmail={userEmail}
                />
                </Grid>
            </Grid>
        )
    }

    return (
        <div className="uccContainer">
            <div className='uccTitleContainer' ref={backgroundTableRef}>
                <h5>{bgvString.BACKGROUND_CHECK}</h5>
            </div>
            <TableComponent
                keyField='refId'
                columns={bgvColumns}
                data={bgvList}
                showPagination={false}
                selectRow={!isReadOnlyUser ? selectRow : undefined}
                loading={loading.list}
            />
            <div className='filterContainer'>
                <div className='uccBtnContainer'>
                    {!isReadOnlyUser && <Button
                        endIcon={creditUsageEnabled && creditCosts ?
                            <CreditCost cost={getEndIcon()} /> : undefined}                        variant='contained'
                        color='primary'
                        disabled={selectedRow?.length === 0 || loading.search}
                        onClick={() => openConfirmDialog(
                            'Confirm Background Search',
                            'Are you sure you want to perform a new background search?',
                            handleSearch
                        )}
                    >
                        {
                            loading.search ? <CircularProgress size={24} color='inherit' /> : strings.SEARCH
                        }
                    </Button>}
                </div>
            </div>

            <div className='uccTitleContainer'>
                <h5>{bgvString.CONNECTED_BUSINESS}</h5>
            </div>
            <TableComponent
                className='mb-5'
                keyField='entityUUID'
                columns={bgvColumns}
                data={connectedBuisnessList.filter(data => data.entityType === 'CONNECTED_BUSINESS' && data.status === strings.STATUS_NAMES.Hit_Completed)}
                showPagination={false}
            />

            {/* Drawer to show list of businesses  */}
            <Drawer
                open={openReport.business}
                onClose={() => {
                    setOpenReport(prev => ({ ...prev, business: false }))
                    // Only warn when the search actually surfaced a business we've already saved a
                    // report for — otherwise Report History is empty and the message is misleading.
                    const hasExistingReport = Array.isArray(resData) && resData.some(r => r?.alreadyRequested)
                    if (!isSavedReport && hasExistingReport) {
                        Toast({ type: 'info', message: 'Your search results are not saved. You can continue from Report History.' })
                    }
                }}
                title={`${strings.SEARCH} ${strings.DETAILS}`}
            >
                <div className="box pb-5">
                    <h5>{bgvString.LIST_OF_BUSINESSES}</h5>
                    <Alert severity="info" className="mb-3 mt-1">
                        {bgvString.REPORT_CHARGED_ONCE}
                    </Alert>
                    <TableComponent
                        columns={buisnessBgvColumns}
                        data={resData}
                        showPagination={false}
                        className="customTable"
                    />
                </div>
            </Drawer>

            {/* Drawer to show detailed business report */}
            <Drawer
                open={openReport.detailed}
                onClose={() => setOpenReport(prev => ({ ...prev, detailed: false }))}
                title={strings.APPLICANT.DETAIL_REPORT}
                actions={businessReport?.BestSection ? detailedReportActions : React.cloneElement(detailedReportActions, { hidePdf: true })}
            >
                {buinessReportJSX('business')}
            </Drawer>

            {/* Drawer to show detailed owner report */}
            <Drawer
                open={openReport.owner}
                onClose={() => setOpenReport(false)}
                title='Background Report'
                actions={resData?.BestInfo ? (
                    <div className="d-flex">
                        {detailedReportActions}
                        {!isReadOnlyUser && !isSavedReport && selectedRow[0] ?
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={handleOwnerSave}
                                disabled={loading.save}
                            >
                                {loading.save ? <CircularProgress size={24} color='inherit' /> : strings.Save}
                            </Button> : null}
                    </div>
                ) : isSavedReport ? React.cloneElement(detailedReportActions, { hidePdf: true }) : null}
            >
                {Object.entries(resData).length ?
                    <OwnerReport person={resData} />
                    : (
                        <div className='emptyIcon'>
                            <img src={emptyUCC} alt="no data icon" />
                            <div className="noResultsMessageDiv">
                                <p className="noResultsText boldText">{bgvString.THERE_WERE_NO_RESULTS}</p>
                                <p className="noResultsText">{bgvString.NO_RESULTS_FOR_OWNER}</p>
                            </div>
                        </div>
                    )}

            </Drawer>

            {/* Drawer to show connected business report */}
            {connectedBusinessReport.map(({ key }, index) =>
                <Drawer
                    key={index}
                    open={Boolean(openConnectedBusinessReport[key])}
                    onClose={() => setOpenConnectedBusinessReport(prev => ({ ...prev, [key]: false }))}
                    title={bgvString.CONNECTED_BUSINESS_REPORT}
                    actions={!isReadOnlyUser &&
                        <Button
                            className='loader-width-64'
                            variant="contained"
                            color='primary'
                            size='small'
                            disabled={connectedBuisnessLoading.save}
                            onClick={() => handleConnectedBusinessSave()}
                        >
                            {connectedBuisnessLoading.save ? <CircularProgress size={24} color='inherit' /> : strings.Save}
                        </Button>
                    }
                >
                    {buinessReportJSX('connectedBusiness', key)}
                </Drawer>
            )}


            {/* Report History Drawer */}
            <BgvReportHistoryDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                reportHistory={reportHistory}
                onSelectReport={handleViewHistoricalReport}
                onDownloadPdf={(pdfUuid, filename) => downloadFile(applicationId, pdfUuid, filename)}
            />

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={closeConfirmDialog}
                onConfirm={handleConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                loading={loading.perform || loading.save}
            />
        </div>
    )
}
